import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

/**
 * Do the reversible thing, then offer to put it back.
 *
 * Every removal in this product is a soft delete with a working restore route — retiring
 * an employee, removing a unit, a department, a trainer. So the honest shape here is not
 * EventStorii's `deferWithUndo`, which delays the request and cancels it: the change
 * really happens, and Undo really restores it. That is better for a register, because a
 * deferred delete is a lie to anybody else looking at the same screen for six seconds,
 * and it can be lost if the tab closes while it is pending.
 *
 * It also replaces a confirmation dialog with something faster AND safer: a dialog asks
 * you to predict a mistake, an undo lets you see one. The dialogs that remain are the
 * ones for actions that genuinely cannot be undone — revoking a certificate, cancelling a
 * training — and keeping those rare is what makes them mean something.
 */
export async function performWithUndo({
  action, undo, message, undoneMessage, onSettled, duration = 8000,
}) {
  try {
    await action();
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail, "That did not work"));
    return false;
  }
  onSettled?.();
  toast.success(message, {
    duration,
    action: {
      label: "Undo",
      onClick: async () => {
        try {
          await undo();
          toast.success(undoneMessage || "Put back");
        } catch (e) {
          // Saying "undone" when it was not is worse than the original mistake, because
          // the person stops looking.
          toast.error(formatApiError(e.response?.data?.detail, "Could not undo that"));
        }
        onSettled?.();
      },
    },
  });
  return true;
}
